import anime from 'animejs/lib/anime.es.js';

export const SECTION_IDS = ['hero', 'skills', 'achievements', 'contact'] as const;
type SectionId = (typeof SECTION_IDS)[number];

interface Section {
  id: SectionId;
  element: HTMLElement;
  top: number;
  bottom: number;
}

let sections: Section[] = [];
let isScrolling = false;
let lastScrollTime = Date.now();
let scrollTimeout: number | null = null;
let accumulatedDelta = 0;
let lastScrollDirection = 0;
let touchStartY = 0;
const SCROLL_COOLDOWN = 300;
const SCROLL_THRESHOLD = 30;
const TOUCH_THRESHOLD = 30;
const ANIMATION_DURATION = 600;
const BOUNCE_DURATION = 300;

export const initializeSections = () => {
  sections = SECTION_IDS.map(id => {
    const element = document.getElementById(id);
    if (!element) return null;
    const rect = element.getBoundingClientRect();
    return {
      id,
      element,
      top: rect.top + window.scrollY,
      bottom: rect.bottom + window.scrollY,
    };
  }).filter((section): section is Section => section !== null);
};

export const getCurrentSectionIndex = (): number => {
  const scrollPosition = window.scrollY + window.innerHeight / 3;
  return sections.findIndex(
    section => scrollPosition >= section.top && scrollPosition < section.bottom
  );
};

const resetScrollState = () => {
  isScrolling = false;
  accumulatedDelta = 0;
  if (scrollTimeout) {
    window.clearTimeout(scrollTimeout);
    scrollTimeout = null;
  }
};

export const scrollToSection = (targetIndex: number) => {
  if (targetIndex < 0 || targetIndex >= sections.length) return;

  const now = Date.now();
  if (now - lastScrollTime < SCROLL_COOLDOWN || isScrolling) return;

  isScrolling = true;
  lastScrollTime = now;

  const targetSection = sections[targetIndex];
  anime({
    targets: document.scrollingElement || document.documentElement,
    scrollTop: targetSection.element.offsetTop,
    duration: ANIMATION_DURATION,
    easing: 'easeInOutQuad',
    complete: resetScrollState,
  });
};

export const handleScroll = (event: WheelEvent) => {
  event.preventDefault();

  if (isScrolling) return;

  const currentIndex = getCurrentSectionIndex();
  if (currentIndex === -1) {
    const scrollPosition = window.scrollY + window.innerHeight / 3;
    const nearestSection = sections.reduce((nearest, section) => {
      const currentDiff = Math.abs(section.top - scrollPosition);
      const nearestDiff = Math.abs(nearest.top - scrollPosition);
      return currentDiff < nearestDiff ? section : nearest;
    }, sections[0]);

    const nearestIndex = sections.findIndex(s => s.id === nearestSection.id);
    scrollToSection(nearestIndex);
    return;
  }

  const currentDirection = Math.sign(event.deltaY);

  if (currentDirection !== lastScrollDirection) {
    accumulatedDelta = 0;
  }

  accumulatedDelta += Math.abs(event.deltaY);
  lastScrollDirection = currentDirection;

  if (accumulatedDelta >= SCROLL_THRESHOLD) {
    const targetIndex = currentIndex + currentDirection;

    if (targetIndex < 0) {
      anime({
        targets: document.scrollingElement || document.documentElement,
        scrollTop: 0,
        duration: BOUNCE_DURATION,
        easing: 'easeOutElastic(1, .5)',
      });
      accumulatedDelta = 0;
      return;
    }

    if (targetIndex >= sections.length) {
      const lastSection = sections[sections.length - 1];
      anime({
        targets: document.scrollingElement || document.documentElement,
        scrollTop: lastSection.bottom - window.innerHeight,
        duration: BOUNCE_DURATION,
        easing: 'easeOutElastic(1, .5)',
      });
      accumulatedDelta = 0;
      return;
    }

    scrollToSection(targetIndex);
    accumulatedDelta = 0;
  }
};

const handleTouchStart = (event: TouchEvent) => {
  touchStartY = event.touches[0].clientY;
};

const handleTouchMove = (event: TouchEvent) => {
  event.preventDefault();

  if (isScrolling) return;

  const touchY = event.touches[0].clientY;
  const deltaY = touchStartY - touchY;

  const currentIndex = getCurrentSectionIndex();
  if (currentIndex === -1) {
    const scrollPosition = window.scrollY + window.innerHeight / 3;
    const nearestSection = sections.reduce((nearest, section) => {
      const currentDiff = Math.abs(section.top - scrollPosition);
      const nearestDiff = Math.abs(nearest.top - scrollPosition);
      return currentDiff < nearestDiff ? section : nearest;
    }, sections[0]);

    const nearestIndex = sections.findIndex(s => s.id === nearestSection.id);
    scrollToSection(nearestIndex);
    return;
  }

  if (Math.abs(deltaY) >= TOUCH_THRESHOLD) {
    const direction = Math.sign(deltaY);
    const targetIndex = currentIndex + direction;

    if (targetIndex < 0) {
      anime({
        targets: document.scrollingElement || document.documentElement,
        scrollTop: 0,
        duration: BOUNCE_DURATION,
        easing: 'easeOutElastic(1, .5)',
      });
      return;
    }

    if (targetIndex >= sections.length) {
      const lastSection = sections[sections.length - 1];
      anime({
        targets: document.scrollingElement || document.documentElement,
        scrollTop: lastSection.bottom - window.innerHeight,
        duration: BOUNCE_DURATION,
        easing: 'easeOutElastic(1, .5)',
      });
      return;
    }

    scrollToSection(targetIndex);
    touchStartY = touchY;
  }
};

const handleTouchEnd = () => {
  touchStartY = 0;
};

export const initializeScrollHandler = () => {
  setTimeout(initializeSections, 100);

  window.addEventListener('load', initializeSections);
  window.addEventListener('resize', initializeSections);
  window.addEventListener('wheel', handleScroll, { passive: false });
  window.addEventListener('touchstart', handleTouchStart, { passive: true });
  window.addEventListener('touchmove', handleTouchMove, { passive: false });
  window.addEventListener('touchend', handleTouchEnd, { passive: true });

  return () => {
    resetScrollState();
    window.removeEventListener('load', initializeSections);
    window.removeEventListener('resize', initializeSections);
    window.removeEventListener('wheel', handleScroll);
    window.removeEventListener('touchstart', handleTouchStart);
    window.removeEventListener('touchmove', handleTouchMove);
    window.removeEventListener('touchend', handleTouchEnd);
  };
};
